<template>
    <div>
        <custom-table
            :title="$t('pages.ecommerce.product.upgrade_path.title')"
            :subTitle="$t('pages.ecommerce.product.upgrade_path.subtitle')"
            rowKey="id"
            :items="table.data || []"
            :loading="table.loading"
            selectableRows
            :emptyTableText="$t('pages.ecommerce.product.upgrade_path.no_set_path')"
            :columns="fields"
            :actions="tableActions"
            @action="handleClickAction"
            @selectableRows="handleSelectedRow">
            <template v-slot:product_name="{ row: record }">
                {{ record.to_product?.name }}
                <span v-if="record.to_product?.code" class="text-muted">({{ record.to_product.code }})</span>
            </template>
            <template v-slot:actions="{ row: record }">
                <div class="d-flex justify-content-end">
                    <a @click="deleteUpgradePath(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                        </span>
                    </a>
                </div>
            </template>
        </custom-table>

        <div class="modal fade" id="kt_modal_product_upgrade_path" ref="upgradeModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered mw-650px">
                <div class="modal-content">
                    <div class="modal-header">
                        <h2 class="fw-bolder">{{ form.title }}</h2>
                        <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                            <span class="svg-icon svg-icon-1">
                                <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                            </span>
                        </div>
                    </div>
                    <el-form @submit.prevent="onSubmit()" :model="form.data" ref="upgradeForm">
                        <div class="modal-body py-10 px-lg-17">
                            <div class="fv-row mb-1 mt-3">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.product.upgrade_path.cols.product_name') }}</label>
                                <el-form-item prop="to_product_id" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="form.data.to_product_id" filterable class="w-100" :placeholder="$t('common.chooseSelect')">
                                        <el-option
                                            v-for="p in availableProducts"
                                            :key="p.id"
                                            :value="p.id"
                                            :label="p.name + (p.code ? ' (' + p.code + ')' : '')"/>
                                    </el-select>
                                </el-form-item>
                                <small class="text-muted d-block mt-1">{{ $t('pages.ecommerce.product.upgrade_path.target_help') }}</small>
                            </div>
                        </div>

                        <div class="modal-footer flex-center">
                            <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                                <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                <span v-if="form.loading" class="indicator-progress">
                                    {{ $t("messages.wait") }}
                                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </el-form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "ProductUpgradePathList",
    props: ['productID'],
    components: {CustomTable},
    data() {
        return {
            fields: [
                {name: this.$t("pages.ecommerce.product.upgrade_path.cols.product_name"), scopedSlots: {customRender: "product_name"}},
                {name: this.$t("common.action"), key: "action", scopedSlots: {customRender: "actions"}},
            ],
            tableActions: [
                {name: "new", icon: "bi-plus-lg", label: "btn.add"},
                {name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh"},
                {name: "delete", icon: "bi-trash", label: "btn.delete"}
            ],
            selectedRowKeys: [],
            form: {
                title: '',
                loading: false,
                data: {},
            },
        }
    },
    computed: {
        table() {
            return this.$store.state.ecommerce.product.upgrade_path.table;
        },
        allProducts() {
            return this.$store.state.ecommerce.product.table?.data || [];
        },
        availableProducts() {
            const usedIds = (this.table.data || []).map(p => p.to_product_id);
            const selfId = Number(this.productID);
            return this.allProducts.filter(p =>
                p.id !== selfId
                && p.product_type_id === 1
                && !usedIds.includes(p.id)
            );
        },
    },
    mounted() {
        this.refreshList();
        this.$store.dispatch('ecommerce/product/get', {filterData: {page_limit: 9999, product_type_id: 1}});
    },
    methods: {
        refreshList() {
            this.$store.dispatch('ecommerce/product/upgrade_path/get', {product_id: this.productID});
        },
        addUpgradePath() {
            this.resetForm();
            this.form.title = this.$t('pages.ecommerce.product.upgrade_path.newTitle');
            this.showModal(this.$refs.upgradeModal);
        },
        deleteUpgradePath(record) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('btn.delete'),
                message: this.$t('messages.sureDelete'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/product/upgrade_path/delete', {
                        id: record.id,
                        product_id: this.productID,
                    });
                    if (response?.status) {
                        this.onResponse(response, () => this.refreshList());
                    }
                },
            });
        },
        handleClickAction(name) {
            switch (name) {
                case "new":
                    this.addUpgradePath();
                    break;
                case "refresh":
                    this.refreshList();
                    break;
                case "delete":
                    this.bulkDelete();
                    break;
            }
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        async bulkDelete() {
            const ids = this.selectedRowKeys.flat();
            if (!ids.length) {
                this.$message.warning(this.$t('messages.selectRow'));
                return;
            }
            for (const id of ids) {
                await this.$store.dispatch('ecommerce/product/upgrade_path/delete', {
                    id,
                    product_id: this.productID,
                });
            }
            this.selectedRowKeys = [];
            this.refreshList();
        },
        onSubmit() {
            this.$refs.upgradeForm.validate(async (valid) => {
                if (!valid) return false;

                this.form.loading = true;

                try {
                    const response = await this.$store.dispatch('ecommerce/product/upgrade_path/add', {
                        product_id: this.productID,
                        formData: {
                            to_product_id: this.form.data.to_product_id,
                        },
                    });

                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.hideModal(this.$refs.upgradeModal);
                            this.refreshList();
                        });
                    }
                } finally {
                    this.form.loading = false;
                }
            });
        },
        resetForm() {
            this.form = {
                title: '',
                loading: false,
                data: {
                    to_product_id: undefined,
                },
            };
        },
    }
}
</script>
